<script setup lang="ts">
    import { useDashboardStore } from '@/js/stores/dashboard';
    import { computed, ref, watch } from 'vue';
    import { __ } from '../../stores/expressions';
    import Modal, { ModalCloseColor, ModalSize } from '../Modal.vue';
    import EchoButton from '../EchoButton.vue';
    import Grid from '../grid/Grid.vue';
    import ShareDashboardUsers from './shareDashboard/ShareDashboardUsers.vue';
    import ShareDashboardRoles from './shareDashboard/ShareDashboardRoles.vue';
    import ShareDashboardLinks from './shareDashboard/ShareDashboardLinks.vue';
    import ShareDashboardAllShares from './shareDashboard/ShareDashboardAllShares.vue';
    import EnumToggleSwitch from '../EnumToggleSwitch.vue';
    import { UserShare } from '@/js/services/api/shareDashboard/types';
    import { useConfirmationDialog } from '@/js/stores/confirmationDialog';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { PermissionArea, DashboardModulePermissionFlag } from '@/js/consts/permissions';
    import { ChevronRightIcon } from '@lucide/vue';

    const { hasPermission } = usePermissions();

    const canShareWithUsers = hasPermission(
        PermissionArea.DashboardModule,
        DashboardModulePermissionFlag.ShareWithUsers
    );

    const canShareWithRoles = hasPermission(
        PermissionArea.DashboardModule,
        DashboardModulePermissionFlag.ShareWithRoles
    );

    const canShareUrl = hasPermission(
        PermissionArea.DashboardModule,
        DashboardModulePermissionFlag.ShareUrl
    );

    const props = defineProps<{
        show: boolean;
        initialView?: 'users' | 'roles' | 'links' | 'all-shares';
    }>();

    const emit = defineEmits<{
        (e: 'close'): void;
    }>();

    const dashboard = useDashboardStore();
    const selectedDashboard = computed(() => dashboard.selectedDashboard);
    const confirm = useConfirmationDialog();

    const previewAs = ref<UserShare | null>(null);
    const showAllShares = ref<boolean>(false);
    const activeSection = ref<'users' | 'roles' | 'links'>('users');

    const sectionItems = computed(() => {
        const items: { value: 'users' | 'roles' | 'links'; label: string }[] = [];
        if (canShareWithUsers.value) items.push({ value: 'users', label: __('users') });
        if (canShareWithRoles.value) items.push({ value: 'roles', label: __('roles') });
        if (canShareUrl.value) items.push({ value: 'links', label: __('links') });
        return items;
    });

    const linksRef = ref<InstanceType<typeof ShareDashboardLinks> | null>(null);
    const usersRef = ref<InstanceType<typeof ShareDashboardUsers> | null>(null);
    const rolesRef = ref<InstanceType<typeof ShareDashboardRoles> | null>(null);

    const hasCurrentViewUnsavedChanges = (): boolean => {
        if (showAllShares.value) {
            return false;
        }
        if (activeSection.value === 'links') {
            return linksRef.value?.hasUnsavedChanges ?? false;
        }
        if (activeSection.value === 'roles') {
            return rolesRef.value?.hasUnsavedChanges ?? false;
        }
        return usersRef.value?.hasUnsavedChanges ?? false;
    };

    const confirmDiscardChanges = async (): Promise<boolean> => {
        if (!hasCurrentViewUnsavedChanges()) return true;
        return confirm.openDialog({
            title: __('unsaved-changes-title'),
            message: __('unsaved-changes-message'),
            confirmText: __('discard-changes'),
        });
    };

    const openAllShares = async () => {
        if (!(await confirmDiscardChanges())) return;
        showAllShares.value = true;
    };

    const closeAllShares = () => {
        showAllShares.value = false;
    };

    const openSectionFromAllShares = async (section: 'users' | 'roles' | 'links') => {
        closeAllShares();
        activeSection.value = section;
    };

    const trySetSection = async (next: string | number | undefined) => {
        const target = next === 'roles' ? 'roles' : next === 'links' ? 'links' : 'users';
        if (target === activeSection.value) return;
        if (!(await confirmDiscardChanges())) return;
        activeSection.value = target;
    };

    watch(
        () => props.show,
        (isVisible) => {
            if (!isVisible) return;

            if (props.initialView === 'all-shares') {
                showAllShares.value = true;
                return;
            }

            showAllShares.value = false;

            if (props.initialView === 'links' && canShareUrl.value) {
                activeSection.value = 'links';
                return;
            }

            if (props.initialView === 'roles' && canShareWithRoles.value) {
                activeSection.value = 'roles';
                return;
            }

            if (props.initialView === 'users') {
                if (canShareWithUsers.value) {
                    activeSection.value = 'users';
                } else if (canShareWithRoles.value) {
                    activeSection.value = 'roles';
                } else if (canShareUrl.value) {
                    activeSection.value = 'links';
                } else {
                    showAllShares.value = true;
                }
            }
        }
    );

    const beforeModalClose = async (): Promise<boolean> => {
        return confirmDiscardChanges();
    };

    const onClose = () => {
        previewAs.value = null;
        showAllShares.value = false;
        activeSection.value = 'users';
        emit('close');
    };
</script>
<template>
    <Modal
        :show="props.show"
        :close-color="ModalCloseColor.Light"
        :size="ModalSize.Full"
        :before-close="beforeModalClose"
        @close="onClose"
    >
        <div v-if="selectedDashboard" class="-m-8">
            <div class="lg:flex h-[calc(100vh-1.5rem)] sm:h-[calc(100vh-5rem)]">
                <div class="p-8 w-full lg:w-2xl flex flex-col">
                    <div
                        v-if="!showAllShares"
                        class="flex flex-wrap items-start justify-between border-b border-gray-light mb-4"
                    >
                        <div class="mb-4">
                            <h1 class="heading-lg">
                                {{ __('share') }}
                            </h1>
                            <p class="text-body text-gray-dark">
                                {{ selectedDashboard?.title }}
                            </p>
                        </div>

                        <nav class="-mt-2">
                            <ul class="flex items-center">
                                <li>
                                    <EchoButton variant="tertiary" @click="openAllShares">
                                        {{ __('show-all-shares') }}
                                        <template #icon-right><ChevronRightIcon /></template>
                                    </EchoButton>
                                </li>
                            </ul>
                        </nav>
                    </div>

                    <EnumToggleSwitch
                        v-if="!showAllShares && sectionItems.length > 1"
                        id="share-dashboard-section"
                        :items="sectionItems"
                        :model-value="activeSection"
                        :spread="true"
                        class="mb-4"
                        @update:model-value="trySetSection"
                    />

                    <div class="flex-grow flex flex-col">
                        <ShareDashboardAllShares
                            v-if="showAllShares"
                            :dashboard-id="selectedDashboard.id"
                            @close="closeAllShares"
                            @navigate-users="openSectionFromAllShares('users')"
                            @navigate-roles="openSectionFromAllShares('roles')"
                            @navigate-links="openSectionFromAllShares('links')"
                        />
                        <ShareDashboardLinks
                            v-else-if="activeSection === 'links' && canShareUrl"
                            ref="linksRef"
                            :dashboard-id="selectedDashboard.id"
                        />
                        <ShareDashboardRoles
                            v-else-if="activeSection === 'roles' && canShareWithRoles"
                            ref="rolesRef"
                            :dashboard-id="selectedDashboard.id"
                        />
                        <ShareDashboardUsers
                            v-else-if="canShareWithUsers"
                            ref="usersRef"
                            v-model:preview-as="previewAs"
                            :dashboard-id="selectedDashboard.id"
                        />
                        <ShareDashboardRoles
                            v-else-if="canShareWithRoles"
                            ref="rolesRef"
                            :dashboard-id="selectedDashboard.id"
                        />
                    </div>
                </div>

                <div class="hidden p-8 primary-gradient flex-grow lg:flex flex-col">
                    <h2 class="text-white mb-2 heading-md-light">
                        <template v-if="!previewAs">
                            {{ __('dashboard-preview') }}
                        </template>
                        <template v-else>
                            {{
                                __('dashboard-preview-as', {
                                    user: `${previewAs?.name} (${previewAs?.role?.name}, ${previewAs?.email})`,
                                })
                            }}
                        </template>
                    </h2>

                    <div class="bg-white flex-grow rounded-sm p-4 overflow-auto custom-scrollbar">
                        <div v-if="selectedDashboard">
                            <Grid
                                :dashboard-id="selectedDashboard.id"
                                :widget-data="selectedDashboard.widgets"
                                :widget-layout="selectedDashboard.widgetLayout"
                                :static-grid="true"
                                container="modal"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Modal>
</template>
